import { useEffect, useState } from "react";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";

// Staff preview + send of the side-by-side color comparison email.
export const ColourOptionsDialog = ({ open, onOpenChange, quoteId, defaultTo }) => {
  const [to, setTo] = useState(defaultTo || "");
  const [message, setMessage] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !quoteId) return undefined;
    setError("");
    const t = setTimeout(() => {
      api
        .post(`/quotes/${quoteId}/colour-options/preview`, { to, message })
        .then(({ data }) => {
          setPreview(data);
          if (!to && data.to) setTo(data.to);
        })
        .catch((e) => setError(formatApiErrorDetail(e) || "Could not build the preview"));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, quoteId, message]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/colour-options/send`, { to, message });
      toast.success(`Color options sent to ${data.sent_to}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not send");
    }
    setBusy(false);
  };

  const colors = (preview?.areas || []).reduce((n, a) => n + a.choices.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl" data-testid="colour-options-dialog">
        <DialogHeader>
          <DialogTitle>Email the color comparison</DialogTitle>
          <DialogDescription>
            The customer gets the colors side by side with the price difference and a link to pick one from their phone.
            {preview?.sent?.at ? ` Last sent ${preview.sent.at.slice(0, 10)} to ${preview.sent.to}.` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-[1fr_1.4fr]">
          <div className="space-y-3">
            <label className="block text-xs font-semibold text-zinc-400">
              Send to
              <Input
                data-testid="colour-options-to"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="customer@email.com"
                className="mt-1"
              />
            </label>
            <label className="block text-xs font-semibold text-zinc-400">
              Personal note (optional)
              <Textarea
                data-testid="colour-options-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                placeholder="e.g. Taj Mahal is in stock and can template next week."
                className="mt-1"
              />
            </label>
            {preview && (
              <p className="text-xs text-zinc-500" data-testid="colour-options-summary">
                {preview.areas.length} area{preview.areas.length === 1 ? "" : "s"} · {colors} colors ·{" "}
                <a href={preview.link} target="_blank" rel="noreferrer" className="underline">
                  open the pick page
                </a>
              </p>
            )}
            {error && (
              <p className="text-xs text-red-400" data-testid="colour-options-error">{error}</p>
            )}
            <Button
              data-testid="colour-options-send"
              onClick={send}
              disabled={busy || !preview || !to}
              className="w-full gap-2"
            >
              <PaperPlaneTilt size={16} weight="bold" />
              {busy ? "Sending…" : "Send comparison"}
            </Button>
          </div>
          <div className="overflow-hidden rounded-lg border border-zinc-800 bg-white">
            {preview ? (
              <iframe
                title="Email preview"
                data-testid="colour-options-preview"
                srcDoc={preview.html}
                className="h-[520px] w-full"
              />
            ) : (
              <div className="p-6 text-sm text-zinc-500">{error ? "" : "Building preview…"}</div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
